import React, { useEffect, useState } from 'react';
import { DollarSign, X } from 'lucide-react';
import { getDefaultCurrency } from '../../utils/formatMoney';

const PAYMENT_METHODS = [
    { value: 'cash', label: 'Cash' },
    { value: 'eftpos', label: 'EFTPOS' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'card', label: 'Card (manual)' },
    { value: 'stripe', label: 'Stripe (manual reference)' },
    { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
    { value: 'assigned', label: 'Assigned — vendor confirmed, ready to proceed' },
    { value: 'advance_paid', label: 'Advance Paid — payment received, assign driver next' },
    { value: 'pending', label: 'Pending — keep in queue' },
];

const UpdatePaymentModal = ({
    isOpen,
    onClose,
    onSubmit,
    serviceNumber,
    defaultAmount,
    currency,
    currentStatus,
    vendorAssigned = false,
    existingReference = '',
}) => {
    const currencyCode = (currency || getDefaultCurrency()).toUpperCase();
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('eftpos');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [updateStatus, setUpdateStatus] = useState(true);
    const [targetStatus, setTargetStatus] = useState('advance_paid');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setAmount(defaultAmount != null && !Number.isNaN(Number(defaultAmount)) ? String(defaultAmount) : '');
            setPaymentMethod('eftpos');
            setReference(existingReference || '');
            setNotes('');
            setUpdateStatus(true);
            setTargetStatus(
                vendorAssigned || currentStatus === 'awaiting_customer_payment'
                    ? 'assigned'
                    : currentStatus === 'assigned'
                      ? 'assigned'
                      : 'advance_paid',
            );
            setError('');
        }
    }, [isOpen, defaultAmount, currentStatus, vendorAssigned, existingReference]);

    if (!isOpen) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        const parsedAmount = Number(amount);
        if (!parsedAmount || parsedAmount <= 0) {
            setError('Enter a valid payment amount.');
            return;
        }
        if (!reference.trim()) {
            setError('Payment reference / transaction ID is required.');
            return;
        }

        setLoading(true);
        try {
            await onSubmit({
                amount: parsedAmount,
                payment_method: paymentMethod,
                provider_payment_id: reference.trim(),
                notes: notes.trim() || null,
                update_service_status: updateStatus,
                target_status: targetStatus,
            });
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to record payment');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
                <div className="flex items-center justify-between border-b px-6 py-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Update Payment Details</h3>
                        {serviceNumber ? (
                            <p className="text-sm text-gray-500 mt-0.5">Service {serviceNumber}</p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded bg-red-500 p-1 text-white hover:bg-red-600"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 p-6">
                    <p className="text-sm text-gray-600">
                        Record cash, EFTPOS, or other offline payments when Stripe auto-update fails or the customer paid outside the link.
                    </p>

                    {error ? (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    ) : null}

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                            Amount ({currencyCode}) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                            Payment Method <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {PAYMENT_METHODS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                            Payment Reference / Transaction ID <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="EFTPOS ref, receipt no., bank txn id…"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Notes (optional)</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder="Any additional notes for audit trail"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                        <label className="flex items-center gap-2 text-sm text-gray-800">
                            <input
                                type="checkbox"
                                checked={updateStatus}
                                onChange={(e) => setUpdateStatus(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Update service status after recording payment
                        </label>

                        {updateStatus ? (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Service status</label>
                                <select
                                    value={targetStatus}
                                    onChange={(e) => setTargetStatus(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                            <DollarSign className="h-4 w-4" />
                            {loading ? 'Saving…' : 'Save Payment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdatePaymentModal;
